// OpenAI Realtime transcription over WebRTC.
//
// The authenticated Apolo Worker negotiates SDP, then microphone audio travels
// directly from Chromium/Electron to OpenAI. Transcript deltas arrive through
// the WebRTC data channel while the user is still speaking.

import { safeFetch } from './safeFetch.js';
import { createTranscriptTracker } from './transcriptTracker.js';

const CONNECT_TIMEOUT_MS = 15_000;
const FINAL_TRANSCRIPT_TIMEOUT_MS = 8_000;

function withTimeout(promise, ms, message, setTimer = setTimeout, clearTimer = clearTimeout) {
  return new Promise((resolve, reject) => {
    const timer = setTimer(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimer(timer);
        resolve(value);
      },
      (error) => {
        clearTimer(timer);
        reject(error);
      },
    );
  });
}

export function realtimeErrorMessage(event) {
  return (
    event?.error?.message ||
    event?.message ||
    event?.error?.code ||
    'Falha na transcrição em tempo real'
  );
}

function defaultSetTimeout(handler, timeout) {
  return globalThis.setTimeout(handler, timeout);
}
function defaultClearTimeout(id) {
  return globalThis.clearTimeout(id);
}

export class RealtimeTranscriber {
  constructor({
    sessionUrl,
    token,
    language = 'pt',
    onDelta,
    onCompleted,
    onError,
    fetchImpl = safeFetch,
    PeerConnection = globalThis.RTCPeerConnection,
    getUserMedia = (constraints) => navigator.mediaDevices.getUserMedia(constraints),
    setTimer = defaultSetTimeout,
    clearTimer = defaultClearTimeout,
  }) {
    this.sessionUrl = sessionUrl;
    this.token = token;
    this.language = language;
    this.onDelta = onDelta;
    this.onCompleted = onCompleted;
    this.onError = onError;
    this.fetchImpl = fetchImpl;
    this.PeerConnection = PeerConnection;
    this.getUserMedia = getUserMedia;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;

    this.pc = null;
    this.channel = null;
    this.stream = null;
    this.finalResolver = null;
    this.finalRejecter = null;
    this.hadDelta = false;
    this.accumulated = '';
    this.closed = false;
    this.trackTranscript = createTranscriptTracker();
    this.abortController = new AbortController();
  }

  async start() {
    if (typeof this.PeerConnection !== 'function') {
      throw new Error('Transcrição em tempo real não suportada neste dispositivo');
    }

    try {
      const stream = await this.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (this.closed) {
        stream.getTracks?.().forEach((track) => track.stop());
        throw new Error('Transcrição cancelada');
      }
      this.stream = stream;
      this.connectTimer = this.setTimer(() => this.abortController.abort(), CONNECT_TIMEOUT_MS);
      const track = this.stream.getAudioTracks?.()[0] || this.stream.getTracks?.()[0];
      if (!track) throw new Error('Microfone não encontrado');

      this.pc = new this.PeerConnection();
      this.pc.addTrack(track, this.stream);
      this.channel = this.pc.createDataChannel('oai-events');
      this.channel.addEventListener('message', (message) => this.handleMessage(message));

      const channelOpen = new Promise((resolve, reject) => {
        this.channel.addEventListener('open', resolve, { once: true });
        this.channel.addEventListener(
          'error',
          () => reject(new Error('Canal de transcrição falhou ao conectar')),
          { once: true },
        );
      });
      // Install a rejection handler immediately: negotiation can still be pending.
      channelOpen.catch(() => {});
      this.channel.addEventListener('close', () => this.handleDisconnect());
      this.pc.addEventListener?.('connectionstatechange', () => {
        if (this.pc?.connectionState === 'failed') this.handleDisconnect();
      });

      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      const response = await this.fetchImpl(
        `${this.sessionUrl}?language=${encodeURIComponent(this.language)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          },
          body: offer.sdp,
          signal: this.abortController.signal,
        },
      );

      const responseText = await response.text();
      if (!response.ok) {
        let message = `OpenAI Realtime ${response.status}`;
        try {
          const data = JSON.parse(responseText);
          message = data?.error || message;
        } catch {
          if (responseText.trim()) message = responseText.slice(0, 200);
        }
        throw new Error(message);
      }

      if (this.closed) throw new Error('Transcrição cancelada');
      await this.pc.setRemoteDescription({ type: 'answer', sdp: responseText });
      await withTimeout(
        channelOpen,
        CONNECT_TIMEOUT_MS,
        'Tempo esgotado ao abrir transcrição',
        this.setTimer,
        this.clearTimer,
      );
    } catch (error) {
      this.close();
      throw error;
    } finally {
      this.clearTimer(this.connectTimer);
    }
  }

  handleMessage(message) {
    if (this.closed) return;
    let event;
    try {
      event = JSON.parse(message?.data);
    } catch {
      return;
    }

    const type = String(event?.type || '');
    // Official + legacy aliases — OpenAI has renamed these across Realtime revisions.
    const isDelta =
      type === 'conversation.item.input_audio_transcription.delta' ||
      type === 'input_audio_transcription.delta' ||
      type === 'transcript.text.delta';
    const isCompleted =
      type === 'conversation.item.input_audio_transcription.completed' ||
      type === 'input_audio_transcription.completed' ||
      type === 'transcript.text.done';

    if (isDelta || isCompleted) {
      const update = this.trackTranscript(event, isCompleted);
      if (!update) return;
      if (isDelta) {
        this.accumulated += update.delta;
        this.onDelta?.(update.delta);
        return;
      }
      if (!update.hadDelta) this.accumulated += update.transcript;
      this.onCompleted?.(update.transcript, { hadDelta: update.hadDelta });
      this.finalResolver?.(update.transcript);
      this.finalResolver = null;
      this.finalRejecter = null;
      return;
    }

    if (type === 'error' || type === 'conversation.item.input_audio_transcription.failed') {
      const error = new Error(realtimeErrorMessage(event));
      this.onError?.(error);
      this.finalRejecter?.(error);
      this.finalResolver = null;
      this.finalRejecter = null;
    }
  }

  handleDisconnect() {
    if (this.closed) return;
    const error = new Error('Conexão de transcrição interrompida');
    this.finalRejecter?.(error);
    this.close();
    this.onError?.(error);
  }

  async stop() {
    if (this.closed) return String(this.accumulated || '').trim();

    this.stream?.getTracks?.().forEach((track) => track.stop());
    if (!this.channel || this.channel.readyState !== 'open') {
      this.close();
      return String(this.accumulated || '').trim();
    }

    const finalTranscript = new Promise((resolve, reject) => {
      this.finalResolver = resolve;
      this.finalRejecter = reject;
    });
    try {
      this.channel.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
    } catch {
      this.close();
      return String(this.accumulated || '').trim();
    }

    try {
      return await withTimeout(
        finalTranscript,
        FINAL_TRANSCRIPT_TIMEOUT_MS,
        'Tempo esgotado ao finalizar transcrição',
        this.setTimer,
        this.clearTimer,
      );
    } catch (err) {
      // Prefer any deltas we already got over surfacing a hard timeout as empty.
      const partial = String(this.accumulated || '').trim();
      if (partial) return partial;
      throw err;
    } finally {
      this.close();
    }
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.abortController.abort();
    this.stream?.getTracks?.().forEach((track) => track.stop());
    try {
      this.channel?.close();
    } catch {
      // already closed
    }
    try {
      this.pc?.close();
    } catch {
      // already closed
    }
    this.channel = null;
    this.pc = null;
    this.stream = null;
  }
}
